import React, { createContext, useContext, useReducer, useEffect } from 'react';
import axios from 'axios';
import api from '../services/api';

// Create auth context
const AuthContext = createContext(null);

// Auth actions
const AUTH_ACTIONS = {
    LOGIN_SUCCESS: 'LOGIN_SUCCESS',
    LOGOUT: 'LOGOUT',
    SET_LOADING: 'SET_LOADING',
    SET_USER: 'SET_USER'
};

// Initial state - check localStorage first
const getInitialState = () => {
    const token = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('user');
    
    if (token && storedUser) {
        try {
            const user = JSON.parse(storedUser);
            return {
                user,
                token,
                isAuthenticated: true,
                loading: true // Will verify token validity
            };
        } catch (error) {
            console.error('Error parsing stored user data:', error);
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
        }
    }
    
    return {
        user: null,
        token: null,
        isAuthenticated: false,
        loading: true // Will check for token on init
    };
};

const initialState = getInitialState();

// Auth reducer
const authReducer = (state, action) => {
    switch (action.type) {
        case AUTH_ACTIONS.LOGIN_SUCCESS:
            return {
                ...state,
                user: action.payload.user,
                token: action.payload.token,
                isAuthenticated: true,
                loading: false
            };
        case AUTH_ACTIONS.LOGOUT:
            return {
                ...state,
                user: null,
                token: null,
                isAuthenticated: false,
                loading: false
            };
        case AUTH_ACTIONS.SET_LOADING:
            return {
                ...state,
                loading: action.payload
            };
        case AUTH_ACTIONS.SET_USER:
            return {
                ...state,
                user: action.payload,
                loading: false
            };
        default:
            return state;
    }
};

// Auth provider component
export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);

    // Set up axios interceptor and restore session
    useEffect(() => {
        const initializeAuth = async () => {
            const token = localStorage.getItem('authToken');
            const storedUser = localStorage.getItem('user');
            
            if (token && storedUser) {
                try {
                    const user = JSON.parse(storedUser);
                    // The api interceptors will handle setting the authorization header
                    
                    // Verify token is still valid by calling /api/auth/me
                    const response = await api.get('/api/auth/me');
                    
                    // Update user data from server in case it changed
                    const updatedUser = response.data;
                    localStorage.setItem('user', JSON.stringify(updatedUser));
                    
                    dispatch({
                        type: AUTH_ACTIONS.LOGIN_SUCCESS,
                        payload: { user: updatedUser, token }
                    });
                    
                    console.log('✅ Authentication restored from localStorage');
                } catch (error) {
                    console.log('❌ Token expired or invalid, logging out...');
                    // Clear invalid token data
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('user');
                    // The api interceptors will handle removing the authorization header
                    dispatch({ type: AUTH_ACTIONS.LOGOUT });
                }
            } else {
                dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
            }
        };

        initializeAuth();

        // Request interceptor
        const requestInterceptor = axios.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('authToken');
                if (token) {
                    config.headers['Authorization'] = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Response interceptor
        const responseInterceptor = axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    logout();
                }
                return Promise.reject(error);
            }
        );

        return () => {
            axios.interceptors.request.eject(requestInterceptor);
            axios.interceptors.response.eject(responseInterceptor);
        };
    }, []);

    const login = async (email, password) => {
        try {
            console.log('🔍 Login attempt with:', { email, password: '***' });
            
            const response = await api.post('/api/auth/login', {
                email,
                password
            });

            const { token, user } = response.data;
            
            localStorage.setItem('authToken', token);
            localStorage.setItem('user', JSON.stringify(user));
            
            // The api interceptors will handle setting the authorization header
            
            dispatch({
                type: AUTH_ACTIONS.LOGIN_SUCCESS,
                payload: { user, token }
            });

            return { success: true };
        } catch (error) {
            // Detailed error logging for debugging
            console.error('❌ Login error details:');
            console.error('Error object:', error);
            console.error('Response status:', error.response?.status);
            console.error('Response headers:', error.response?.headers);
            console.error('Response data:', error.response?.data);
            console.error('Request config:', error.config);
            
            // Log specific 400 error details
            if (error.response?.status === 400) {
                console.error('🔍 400 Bad Request Details:');
                console.error('Request URL:', error.config?.url);
                console.error('Request method:', error.config?.method);
                console.error('Request data sent:', error.config?.data);
                console.error('Response message:', error.response?.data?.message);
                console.error('Response errors:', error.response?.data?.errors);
                console.error('Full response data:', JSON.stringify(error.response?.data, null, 2));
            }
            
            return {
                success: false,
                error: error.response?.data?.message || 'Login failed'
            };
        }
    };

    const register = async (userData) => {
        try {
            console.log('🔍 Registration attempt for:', userData.email);
            await api.post('/api/auth/register', userData);
            return { success: true };
        } catch (error) {
            // Detailed error logging for debugging
            console.error('❌ Registration error details:');
            console.error('Error object:', error);
            console.error('Response status:', error.response?.status);
            console.error('Response data:', error.response?.data);
            
            if (error.response?.status === 400) {
                console.error('🔍 400 Bad Request Details:');
                console.error('Request URL:', error.config?.url);
                console.error('Request data sent:', error.config?.data);
                console.error('Response message:', error.response?.data?.message);
                console.error('Response errors:', error.response?.data?.errors);
                console.error('Full response data:', JSON.stringify(error.response?.data, null, 2));
            }
            
            return {
                success: false,
                error: error.response?.data?.message || 'Registration failed'
            };
        }
    };

    const logout = () => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        // The api interceptors will handle removing the authorization header
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
    };

    const getCurrentUser = async () => {
        try {
            const response = await api.get('/api/auth/me');
            dispatch({
                type: AUTH_ACTIONS.SET_USER,
                payload: response.data
            });
        } catch (error) {
            console.error('❌ Error getting current user:', error);
            logout();
        }
    };

    const hasPermission = (permission) => {
        // SuperAdmin gets all permissions
        if (state.user?.roles?.includes('SuperAdmin')) {
            return true;
        }
        
        const hasPermissionResult = state.user?.permissions?.includes(permission) || false;
        return hasPermissionResult;
    };

    const hasRole = (role) => {
        return state.user?.roles?.includes(role) || false;
    };

    const updateUserInfo = (updatedUser) => {
        const newUser = { ...state.user, ...updatedUser };
        dispatch({
            type: AUTH_ACTIONS.SET_USER,
            payload: newUser
        });
        // Update localStorage as well
        localStorage.setItem('user', JSON.stringify(newUser));
    };

    const refreshUserPermissions = async () => {
        try {
            console.log('Refreshing user permissions...');
            const response = await api.get('/api/auth/me');
            const updatedUser = response.data;
            
            console.log('Updated user permissions:', updatedUser.permissions);
            
            dispatch({
                type: AUTH_ACTIONS.SET_USER,
                payload: updatedUser
            });
            localStorage.setItem('user', JSON.stringify(updatedUser));
            
            return updatedUser;
        } catch (error) {
            console.error('Error refreshing user permissions:', error);
            throw error;
        }
    };

    const value = {
        ...state,
        login,
        register,
        logout,
        getCurrentUser,
        hasPermission,
        hasRole,
        updateUserInfo,
        refreshUserPermissions
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Hook to use auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};